import React, { KeyboardEventHandler } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  Box,
  Button,
  IconButton,
  styled,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";

import { MessageChannel } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import { muiField } from "@/utils/hookForm";
import { directUpload } from "@/utils/upload";

import AttachmentErrDialogExtension from "./AttachmentErrDialogExtension";
import AttachmentErrDialogSize from "./AttachmentErrDialogSize";
import ToolbarFile, { AttachmentFile } from "./ToolbarFile";
import { useSendMessage } from "../../api";

interface Props {
  chatId: string;
  channel: MessageChannel;
  messageId?: string | null;
  onSend: () => void;
}

interface FormValues {
  message: string;
  attachments: AttachmentFile[];
}

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    padding: theme.spacing(3, 4),
    minHeight: 80,
    backgroundColor: colors.white,
  },
  attachIconButton: {
    "&.Mui-disabled": {
      color: "rgba(0,0,0,0.26)",
    },
  },
  files: {
    display: "flex",
    overflowX: "auto",
    overflowY: "hidden",
    marginBottom: theme.spacing(3),
  },
}));

const MessageField = withStyles(TextField, {
  root: {
    width: "100%",
    minHeight: 50,
    "& .MuiOutlinedInput-root": {
      minHeight: 50,
      backgroundColor: "#F7F9FB",
    },
  },
});

const HelperText = withStyles(Typography, (theme) => ({
  root: {
    display: "inline",
    marginRight: theme.spacing(2),
  },
}));

const FileInput = styled("input")({
  display: "none",
});

const AttachIcon = withStyles(AttachFileIcon, {
  root: {
    transform: "rotate(45deg)",
  },
});

const ALLOWED_FILE_TYPES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xslx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  "application/msword", //.doc
  "application/excel", // .xls
  "application/vnd.ms-excel", // .xls
  "application/mspowerpoint", //.ppt
  "application/powerpoint", //.ppt
  "application/vnd.ms-powerpoint", //.ppt
  "application/pdf", // .pdf
  "image/jpeg", // .jpg
  "image/png", // .png
  "video/mp4", // .mp4
  // on my local machine, pptx files isn't selectable in file dialog if we only
  // provide its mimetype in the `accept` attribute. So we also add the filename
  // extension to make it appear. The extension for the other mimetypes were
  // added too, just in case that this issue appear on those mimetypes too.
  ".ppt",
  ".pptx",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".mp4",
];

function fileIsAllowed(file: File): boolean {
  return ALLOWED_FILE_TYPES.includes(file.type);
}

function fileIsNotAllowed(file: File): boolean {
  return !fileIsAllowed(file);
}

function isNotNull<T>(value?: T | null): value is T {
  return value != null;
}

const Toolbar: React.FC<Props> = ({ chatId, channel, messageId, onSend }) => {
  const { classes } = useStyles();
  const extensionErrorDialog = useDialogState();
  const sizeErrorDialog = useDialogState();
  const snackbar = useGlobalSnackbar();
  const {
    register,
    watch,
    control,
    getValues,
    handleSubmit,
    reset,
    formState,
  } = useForm<FormValues>({
    defaultValues: {
      message: "",
      attachments: [],
    },
  });
  const attachments = useFieldArray({ control, name: "attachments" });

  const sendMessage = useSendMessage();
  function send(values: FormValues) {
    if (
      values.message.trim() === "" &&
      (values.attachments.length === 0 ||
        values.attachments.some((a) => !a.signedId))
    ) {
      return;
    }

    reset({ message: "", attachments: [] });
    onSend();
    return sendMessage({
      attributes: {
        chatId,
        channel,
        body: values.message,
        attachments: values.attachments
          .map((a) => a.signedId)
          .filter(isNotNull),
        parentMessageId: messageId,
      },
    }).catch((e) => {
      snackbar.open(
        displayError(e, "Unable to send message. Please try again later.")
      );
      reset(values); // restore previous values
    });
  }

  const handleKeyDown: KeyboardEventHandler<HTMLFormElement> = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(send)();
    }
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file == null) return;

    if (fileIsNotAllowed(file)) {
      extensionErrorDialog.show();
    } else {
      const index = attachments.fields.length;
      attachments.append({ file, _id: nanoid() });
      directUpload({
        file,
        onError: (error) => {
          console.error(error);
          snackbar.open(`Unable to upload ${file.name}`);
          attachments.remove(index);
        },
        onFinish: (blob) => {
          attachments.update(index, {
            ...getValues("attachments")[index],
            signedId: blob.signed_id,
          });
        },
      });
    }

    // Reset file input
    e.target.value = "";
  };

  const isStillUploading = watch("attachments").some((a) => !a.signedId);

  return (
    <>
      <form
        className={classes.root}
        onKeyDown={handleKeyDown}
        onSubmit={handleSubmit(send)}
      >
        <div className={classes.files}>
          {watch("attachments").map((attachment, index) => (
            <Box key={attachment._id} marginRight={4}>
              <ToolbarFile
                attachment={attachment}
                onRemove={() => attachments.remove(index)}
              />
            </Box>
          ))}
        </div>
        <Box display="flex" alignItems="flex-start">
          <Controller
            control={control}
            name="message"
            render={({ field }) => (
              <MessageField
                {...muiField(field)}
                placeholder="Send message"
                size="small"
                multiline
                maxRows={10}
                helperText={
                  <>
                    <HelperText variant="caption" display="inline">
                      Enter to send
                    </HelperText>
                    <HelperText variant="caption" display="inline">
                      Shift + enter to add a new line
                    </HelperText>
                  </>
                }
              />
            )}
          />
          <Box paddingX={2}>
            <label htmlFor="upload-attachment">
              <FileInput
                ref={register("attachments").ref}
                name="attachments"
                id="upload-attachment"
                type="file"
                accept={ALLOWED_FILE_TYPES.join()}
                onChange={handleFileChange}
                aria-hidden="true"
              />
              <IconButton
                className={classes.attachIconButton}
                component="span"
                size="large"
              >
                <AttachIcon />
              </IconButton>
            </label>
          </Box>
          <Box paddingY="7px" paddingRight="13px">
            <Tooltip
              placement="top"
              title={
                isStillUploading ? "Please wait for file uploads to finish" : ""
              }
            >
              <span>
                <Button
                  type="submit"
                  disabled={formState.isSubmitting || isStillUploading}
                >
                  Send
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </form>

      <AttachmentErrDialogSize
        message="Uploaded file is too large"
        open={sizeErrorDialog.shown}
        onClose={sizeErrorDialog.hide}
      />
      <AttachmentErrDialogExtension
        open={extensionErrorDialog.shown}
        onClose={extensionErrorDialog.hide}
      />
    </>
  );
};

export default Toolbar;
